import {
  type InfiniteData,
  type QueryClient,
  hashKey,
  useQueryClient,
} from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { findingsKey, openFindingCount, useFindings } from "./query";
import type { FindingFilter, FindingsPage, FindingStatus } from "./types";

const STATUSES = [
  "open",
  "investigating",
  "action_required",
  "explained",
  "resolved",
] as const satisfies readonly FindingStatus[];

export const REVIEW_FILTERS: { value: FindingFilter; label: string }[] = [
  { value: "open", label: "Active" },
  { value: "closed", label: "Closed" },
  { value: "all", label: "All" },
];

/**
 * Every findings response carries the count of each status. A filter change
 * asks for different rows, and the counts already in hand stay on the chips.
 */
const knownPage = (
  client: QueryClient,
  businessId: string,
  reportId: string,
  scope: { runId: string } | null,
  status?: FindingFilter,
): FindingsPage | undefined => {
  const wanted = hashKey([scope]);
  const [latest] = client
    .getQueryCache()
    .findAll({
      queryKey: [
        ...findingsKey(businessId),
        reportId,
        ...(status ? [status] : []),
      ],
    })
    .filter(
      (query) =>
        hashKey([query.queryKey.at(-1) ?? null]) === wanted &&
        query.state.data !== undefined,
    )
    .sort((a, b) => b.state.dataUpdatedAt - a.state.dataUpdatedAt);
  return latest
    ? client.getQueryData<InfiniteData<FindingsPage>>(latest.queryKey)?.pages[0]
    : undefined;
};

const isFindingFilter = (value: string | null): value is FindingFilter =>
  REVIEW_FILTERS.some((item) => item.value === value);

/**
 * The findings queue. Active, Closed, and All are its mode. A rule from the
 * dashboard narrows the list (`?rule=`). Both live in the URL, so a link
 * opens the same queue.
 */
export const useReviewQueue = (
  businessId: string,
  reportId: string,
  runId: string | null,
) => {
  const client = useQueryClient();
  const [params, setParams] = useSearchParams();
  const asked = params.get("filter");
  const filter: FindingFilter = isFindingFilter(asked) ? asked : "open";
  const selectedRules = params.getAll("rule").flatMap((rule) => {
    const name = rule.trim();
    return name ? [name] : [];
  });
  const setFilter = (value: FindingFilter) =>
    setParams(
      (current) => {
        const next = new URLSearchParams(current);
        if (value === "open") next.delete("filter");
        else next.set("filter", value);
        return next;
      },
      { replace: true },
    );
  const setRules = (rules: string[]) =>
    setParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.delete("rule");
        for (const rule of rules) next.append("rule", rule);
        return next;
      },
      { replace: true },
    );
  const scope = {
    ...(runId ? { runId } : {}),
    ...(selectedRules.length ? { rules: selectedRules } : {}),
  };
  const findings = useFindings(
    businessId,
    reportId,
    filter,
    Object.keys(scope).length ? scope : undefined,
    { keepPrevious: true },
  );
  const pages = findings.isPlaceholderData ? [] : (findings.data?.pages ?? []);
  // This filter's rows may still be out. The counts are not: any list for
  // this period already returned all of them.
  const counts =
    pages[0]?.counts ??
    knownPage(client, businessId, reportId, runId ? { runId } : null)?.counts;
  const ruleCounts =
    pages[0]?.rules ??
    knownPage(client, businessId, reportId, runId ? { runId } : null, filter)
      ?.rules ??
    [];
  const countOf = (value: FindingFilter) => {
    if (!counts) return 0;
    switch (value) {
      case "open":
        return openFindingCount(counts);
      case "closed":
        return (counts.explained ?? 0) + (counts.resolved ?? 0);
      case "all":
        return STATUSES.reduce((sum, status) => sum + (counts[status] ?? 0), 0);
      default: {
        const unreachable: never = value;
        return unreachable;
      }
    }
  };
  return {
    filter,
    setFilter,
    /** The rules the list is narrowed to. Empty is every rule. */
    selectedRules,
    setRules,
    findings,
    items: pages.flatMap((page) => page.findings),
    /** Every rule in this status and period, with how many findings it has. */
    ruleCounts,
    countOf,
    countsReady: counts !== undefined,
  };
};
